import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { FileArchive, Search, Download } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const cfg = { withCredentials: true };
const firstDay = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`; };
const today = () => new Date().toISOString().slice(0, 10);

// Configurações → XML Contador: exporta os XMLs (NFC-e / NF-e) por loja, tipo e período em um ZIP
export const FiscalXmlExportPanel = () => {
  const [stores, setStores] = useState([]);
  const [f, setF] = useState({ store: '', tipo: 'todos', start: firstDay(), end: today() });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState('');

  useEffect(() => {
    axios.get(`${API}/fiscal/xml/stores`, cfg).then((r) => { setStores(r.data); const cur = r.data.find((s) => s.current) || r.data[0]; if (cur) setF((x) => ({ ...x, store: cur.slug })); }).catch(() => {});
  }, []);

  const params = () => new URLSearchParams({ store: f.store, tipo: f.tipo, start: f.start, end: f.end }).toString();
  const doPreview = async () => {
    setBusy('preview'); setPreview(null);
    try { const r = await axios.get(`${API}/fiscal/xml/preview?${params()}`, cfg); setPreview(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Falha ao consultar'); } finally { setBusy(''); }
  };
  const download = async () => {
    setBusy('zip');
    try {
      const r = await axios.get(`${API}/fiscal/xml/zip?${params()}`, { ...cfg, responseType: 'blob' });
      const ok = r.headers['x-xml-ok']; const fail = r.headers['x-xml-fail'];
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url; a.download = `XML-${f.store}-${f.tipo}-${f.start}_a_${f.end}.zip`; a.click(); URL.revokeObjectURL(url);
      toast.success(`ZIP gerado: ${ok} XML(s)${Number(fail) ? ` · ${fail} falha(s) (ver ERROS.txt)` : ''}`);
    } catch (e) {
      let msg = 'Falha ao gerar o ZIP';
      try { msg = JSON.parse(await e.response?.data?.text?.())?.detail || msg; } catch (_x) { /* blob sem JSON */ }
      toast.error(msg);
    } finally { setBusy(''); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md p-4 space-y-4" data-testid="xml-export-panel">
      <div>
        <h3 className="font-bold text-slate-900 flex items-center gap-2"><FileArchive size={16} /> Exportar XMLs para a contabilidade</h3>
        <p className="text-xs text-slate-500 mt-1">Baixa em um único ZIP os XMLs autorizados (e de cancelamento) das NFC-e e NF-e do período, organizados em pastas NFCe/ e NFe/, com a relação em CSV. Os arquivos vêm da Focus NFe.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="space-y-1">
          <Label className="text-xs">Loja</Label>
          <select value={f.store} onChange={(e) => setF({ ...f, store: e.target.value })} className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm" data-testid="xml-store">
            {stores.map((s) => <option key={s.slug} value={s.slug}>{s.name}{s.current ? ' (atual)' : ''}</option>)}
          </select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Tipo de documento</Label>
          <select value={f.tipo} onChange={(e) => setF({ ...f, tipo: e.target.value })} className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm" data-testid="xml-tipo">
            <option value="todos">Todos (NFC-e + NF-e)</option>
            <option value="nfce">NFC-e (modelo 65)</option>
            <option value="nfe">NF-e (modelo 55)</option>
          </select>
        </div>
        <div className="space-y-1"><Label className="text-xs">Data início</Label><Input type="date" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} data-testid="xml-start" /></div>
        <div className="space-y-1"><Label className="text-xs">Data fim</Label><Input type="date" value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} data-testid="xml-end" /></div>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" onClick={doPreview} disabled={!!busy} data-testid="xml-preview-btn"><Search size={14} className="mr-1" /> {busy === 'preview' ? 'Consultando…' : 'Consultar período'}</Button>
        <Button type="button" onClick={download} disabled={!!busy} className="bg-slate-900 hover:bg-black" data-testid="xml-download-btn"><Download size={14} className="mr-1" /> {busy === 'zip' ? 'Gerando ZIP…' : 'Baixar todos os XMLs (ZIP)'}</Button>
      </div>
      {preview && (
        <div className="rounded-md border border-slate-200 p-3 text-sm space-y-2" data-testid="xml-preview">
          <p className="font-bold text-slate-900">{preview.count} documento(s) com XML no período · valor autorizado R$ {Number(preview.total_value).toFixed(2)}</p>
          <p className="text-xs text-slate-600">NFC-e: {preview.by_type.nfce.autorizado} autorizada(s), {preview.by_type.nfce.cancelado} cancelada(s) · NF-e: {preview.by_type.nfe.autorizado} autorizada(s), {preview.by_type.nfe.cancelado} cancelada(s)</p>
          {preview.sample.length > 0 && (
            <div className="max-h-56 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="text-slate-500"><tr><th className="text-left py-1">Tipo</th><th className="text-left">Nº</th><th className="text-left">Venda</th><th className="text-left">Data</th><th className="text-left">Status</th><th className="text-right">Valor</th></tr></thead>
                <tbody>{preview.sample.map((d, i) => (
                  <tr key={i} className="border-t border-slate-100"><td className="py-1 uppercase">{d.kind}</td><td>{d.number}</td><td>#{d.sale_number}</td><td>{new Date(d.created_at).toLocaleDateString('pt-BR')}</td><td className={d.status === 'cancelado' ? 'text-red-600' : 'text-emerald-700'}>{d.status}</td><td className="text-right">R$ {Number(d.total || 0).toFixed(2)}</td></tr>
                ))}</tbody>
              </table>
              {preview.count > 50 && <p className="text-[10px] text-slate-400 mt-1">Mostrando 50 de {preview.count}.</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
